/**
 * @file Switch 开关
 * @description 紧凑布尔开关；自定义外观。
 *
 * @whenToUse 设置项开/关、启用禁用。
 * @whenNotToUse 表单多选项（Checkbox）；互斥枚举（Radio）。
 */

import type { ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/cn.ts'

export type SwitchProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange'
> & {
  /** 是否开启 */
  checked?: boolean
  /** 切换回调 */
  onCheckedChange?: (checked: boolean) => void
}

export function Switch({
  checked = false,
  onCheckedChange,
  className,
  disabled,
  ...rest
}: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={cn(
        'relative inline-flex h-4 w-7 shrink-0 items-center rounded-full border transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'border-primary bg-primary' : 'border-border bg-raised',
        className,
      )}
      onClick={() => onCheckedChange?.(!checked)}
      {...rest}
    >
      <span
        className={cn(
          'pointer-events-none absolute top-px left-0 size-3 rounded-full transition-transform duration-200',
          checked
            ? 'translate-x-3 bg-primary-foreground'
            : 'translate-x-0 bg-foreground',
        )}
        aria-hidden
      />
    </button>
  )
}
